import axios from "@/utils/axios";
import {
  type ApiNode,
  type Edge,
  type Input,
  type RefName,
} from "@/workflow_api/types/core";
import { useToast } from "@elaniintc/goes-design-system";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  resolveNodeValidationError,
  type ValidationError,
} from "./error-handling";

// Read/query hooks and clone/duplicate hooks live in sibling modules to keep
// this file within a readable size. They are re-exported below so every
// existing `@/workflow_api/services/nodes.api` import keeps working unchanged.
export * from "./nodes-read.api";
export * from "./nodes-clone.api";

interface NodeBody {
  ref_name: RefName;
  display_name: string;
  position_x: number;
  position_y: number;
  flow_id: string;
  type:
    | "GENERAL"
    | "IF_CONDITION"
    | "SWITCH_CONDITION"
    | "WEBHOOK"
    | "FORM"
    | "REVISION"
    | "DOCUMENT_GENERATOR"
    | "DIGITAL_SIGNATURE"
    | "PAYMENT"
    | "DATA_CENTER"
    | "EXPEDIENTE"
    | "MULTITHREAD"
    | "WAIT"
    | "NOTIFICATION"
    | "INTEROPERABILITY"
    | "SIGN_DOCUMENT"
    | "N8N"
    | "MERGE"
    | "SUBFLOW"
    | "CONDITIONAL_GROUPING"
    | "ASSOCIATE_USER"
    | "CONFIRM_ASSOCIATE_USER"
    | "RESOLUTION";
  edge?: Edge;
}

export const useCreateNode = (id: string) => {
  const query = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (data: { node: NodeBody }) => {
      const response = await axios.post("/admin/v1/node", { ...data.node });
      return response.data;
    },
    onSuccess() {
      query.invalidateQueries({
        queryKey: ["admin-flow-detail", id],
      });
      toast.showToast({
        title: "Nodo creado",
        description: "El nodo ha sido creado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    },
    onError(error: ValidationError) {
      let title = "Error al crear nodo";
      let description = "Ocurrió un error al intentar crear el nodo.";

      // Check whether this is a specific validation error
      if (error?.response?.status === 422) {
        ({ title, description } = resolveNodeValidationError(
          error?.response?.data?.errors,
        ));
      } else if (error?.response?.status === 403) {
        title = "Sin permisos";
        description = "No tienes permisos para crear nodos en este flujo.";
      }

      toast.showToast({
        title,
        description,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

export interface UpdateNodeBody {
  flow_id?: string;
  ref_name?: RefName;
  display_name?: string;
  type?: string;
  order?: number;
  position_x?: number;
  position_y?: number;
  manual_continue?: boolean;
  edge?: Edge | null;
  edges?: Array<{
    source?: string | null;
    source_handler: string;
    target_handler: string;
    target_input_name?: string;
  }>;
  new_node?: NodeBody;
  inputs?: Input;
  connections?: Record<
    string,
    { display_name: string; target_node: string | null }
  >;
}
export interface UpdateNodePayload {
  node_id: string;
  node: UpdateNodeBody;
}

export const useUpdateNode = (flowId: string, nodeId?: string) => {
  const query = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (data: UpdateNodePayload) => {
      const response = await axios.patch<{ data: ApiNode }>(
        `/admin/v1/node/${data.node_id || nodeId}`,
        {
          ...data.node,
        },
      );
      return response.data;
    },
    onSuccess(_, { node_id }) {
      query.invalidateQueries({ queryKey: ["admin-flow-detail", flowId] });
      query.invalidateQueries({
        queryKey: ["admin-node-id", node_id ?? nodeId],
      });
      query.invalidateQueries({
        queryKey: ["admin-flow-details", node_id ?? nodeId],
      });
      toast.showToast({
        title: "Nodo actualizado",
        description: "El nodo ha sido actualizado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    },
    onError(error: ValidationError) {
      let title = "Error al actualizar nodo";
      let description = "Ocurrió un error al intentar actualizar el nodo.";

      // Check whether this is a specific validation error
      if (error?.response?.status === 422) {
        ({ title, description } = resolveNodeValidationError(
          error?.response?.data?.errors,
        ));
      } else if (error?.response?.status === 404) {
        title = "Nodo no encontrado";
        description = "El nodo que intentas actualizar no existe.";
      } else if (error?.response?.status === 403) {
        title = "Sin permisos";
        description = "No tienes permisos para actualizar este nodo.";
      }

      toast.showToast({
        title,
        description,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

export const useResetNode = () => {
  return useMutation({
    mutationFn: async (data: UpdateNodePayload) => {
      const response = await axios.patch(`/admin/v1/node/${data.node_id}`, {
        ...data.node,
        inputs: [],
      });
      return response.data;
    },
  });
};

export const useDeleteNode = (id: string) => {
  const query = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (data: { node_id: string }) => {
      const response = await axios.delete(`/admin/v1/node/${data.node_id}`);
      return response.data;
    },
    onSuccess() {
      query.invalidateQueries({
        queryKey: ["admin-flow-detail", id],
      });
      toast.showToast({
        title: "Nodo eliminado",
        description: "El nodo ha sido eliminado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    },
    onError() {
      toast.showToast({
        title: "Error al eliminar el nodo",
        description:
          "Ocurrió un error al intentar eliminar el nodo, intente nuevamente.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

export const useMasiveDelete = (id: string) => {
  const query = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (data: { nodes: string[] }) => {
      const response = await axios.post(`/admin/v1/node/multiple`, {
        nodes: data.nodes,
      });
      return response.data;
    },
    onSuccess() {
      query.invalidateQueries({
        queryKey: ["admin-flow-detail", id],
      });
      toast.showToast({
        title: "Nodos eliminados",
        description: "Los nodos han sido eliminados correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    },
    onError() {
      toast.showToast({
        title: "Error al eliminar nodos",
        description: "Ocurrió un error al intentar eliminar los nodos.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

export const useUpdateNodesPositions = () => {
  const toast = useToast();
  return useMutation({
    mutationFn: async (data: {
      nodes: { id: string; position_x: number; position_y: number }[];
    }) => {
      const response = await axios.post(`/admin/v1/node/position`, {
        nodes: data.nodes,
      });
      return response.data;
    },
    onError() {
      toast.showToast({
        title: "Error al actualizar posiciones",
        description:
          "Ocurrió un error al intentar actualizar las posiciones de los nodos.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};
